import { ref, computed, watch, onMounted, toRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable for handling admin authentication UI logic
 * Manages modal display, authentication state, and routing redirects
 */
export function useAdminAuth() {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  
  // Local state
  const showAuthModal = ref(false)
  const redirectPath = ref(null)

  // Computed
  const needsAuthentication = computed(() => {
    return route.meta.requiresAdminAuth && !authStore.isAuthValid
  })

  const isProtectedRoute = computed(() => {
    return route.meta.requiresAdminAuth === true
  })

  // Methods
  const openAuthModal = (intendedPath = null) => {
    redirectPath.value = intendedPath || route.fullPath
    showAuthModal.value = true
  }

  const closeAuthModal = () => {
    showAuthModal.value = false
    redirectPath.value = null
  }

  const onAuthenticated = () => {
    closeAuthModal()
    
    // Handle redirect after successful authentication
    const targetPath = redirectPath.value || route.query.redirect
    
    if (targetPath && targetPath !== route.fullPath) {
      // Remove auth-related query parameters and redirect
      const cleanQuery = { ...route.query }
      delete cleanQuery.needsAuth
      delete cleanQuery.redirect
      
      router.replace({
        path: targetPath.split('?')[0],
        query: cleanQuery
      })
    } else if (route.query.needsAuth) {
      // Clean up the URL by removing needsAuth parameter
      const cleanQuery = { ...route.query }
      delete cleanQuery.needsAuth
      delete cleanQuery.redirect
      
      router.replace({
        path: route.path,
        query: cleanQuery
      })
    }
  }

  const onAuthCancelled = () => {
    closeAuthModal()
    
    // Leave the protected page when the user cancels auth
    if (isProtectedRoute.value) {
      router.push('/')
    }
  }

  const checkAuthRequirement = () => {
    // Check if we need to show auth modal based on route query parameters
    if (route.query.needsAuth === 'true' && needsAuthentication.value) {
      redirectPath.value = route.query.redirect || route.fullPath
      showAuthModal.value = true
    }
  }

  // Watchers
  watch(
    () => route.query.needsAuth,
    (needsAuth) => {
      if (needsAuth === 'true' && needsAuthentication.value) {
        checkAuthRequirement()
      } else if (!needsAuth) {
        closeAuthModal()
      }
    },
    { immediate: true }
  )

  watch(
    () => authStore.isAuthValid,
    (isValid) => {
      if (isValid && showAuthModal.value) {
        onAuthenticated()
      }
    }
  )

  // Lifecycle
  onMounted(() => {
    checkAuthRequirement()
  })

  return {
    // State
    showAuthModal: computed(() => showAuthModal.value),
    redirectPath: computed(() => redirectPath.value),
    
    // Computed
    needsAuthentication,
    isProtectedRoute,
    
    // Auth store state
    isAuthenticated: toRef(authStore, 'isAuthenticated'),
    isAuthValid: toRef(authStore, 'isAuthValid'),
    isLoading: toRef(authStore, 'isLoading'),
    authError: toRef(authStore, 'lastError'),
    
    // Methods
    openAuthModal,
    closeAuthModal,
    onAuthenticated,
    onAuthCancelled,
    logout: authStore.logout,
    extendSession: authStore.extendSession
  }
}

/**
 * Simple composable for checking if user has admin privileges
 * Can be used in templates or other composables
 */
export function useAdminCheck() {
  const authStore = useAuthStore()
  
  return {
    isAdmin: toRef(authStore, 'isAuthValid'),
    needsAuth: toRef(authStore, 'needsAuthentication'),
    isLoading: toRef(authStore, 'isLoading')
  }
}